import { useRouter } from '@tanstack/react-router';
import { mapApiErrorToForm } from '@upme/api-client';
import {
  type CityDto,
  SourceChannelCreateInput,
  type SourceChannelDto,
  SourceChannelUpdateInput,
} from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@upme/ui/components/select';
import { Switch } from '@upme/ui/components/switch';
import { useEffect } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { z } from 'zod';
import { zodResolver } from '../../../app/zod-resolver';
import { useCreateSourceChannel, useUpdateSourceChannel } from '../mutations';

type CreateProps = {
  mode: 'create';
  cities: ReadonlyArray<CityDto>;
};
type EditProps = {
  mode: 'edit';
  channel: SourceChannelDto;
  cities: ReadonlyArray<CityDto>;
  readOnly?: boolean;
};
type Props = CreateProps | EditProps;

type FormValues = SourceChannelCreateInput & Partial<SourceChannelCreateInput>;

export function SourceChannelForm(props: Props) {
  const { t } = useTranslation('source-channels');
  const router = useRouter({ warn: false });
  const isCreate = props.mode === 'create';
  const readOnly = props.mode === 'edit' && props.readOnly === true;
  // Edit mode: RHF stores the full DTO; loosen schema so extra keys (id, createdAt…) don't trip strict.
  const schema = isCreate ? SourceChannelCreateInput : SourceChannelUpdateInput.loose();

  const defaults: Partial<FormValues> = isCreate
    ? ({ isActive: true, fetchCadenceMin: 15, kind: 'EVENTS' } as Partial<FormValues>)
    : (props.channel as unknown as FormValues);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema as unknown as z.ZodType<FormValues>) as Resolver<FormValues>,
    defaultValues: defaults as FormValues,
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (props.mode === 'edit') {
      form.reset(props.channel as unknown as FormValues);
    }
    // Re-run only when the channel identity changes (new server payload), not on every render.
  }, [props.mode === 'edit' ? props.channel.id : null]);

  const create = useCreateSourceChannel();
  const update = useUpdateSourceChannel(isCreate ? '' : props.channel.id);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      if (isCreate) {
        const result = await create.mutateAsync(values as SourceChannelCreateInput);
        toast.success(t('form.createSuccess'));
        router?.navigate({ to: '/source-channels/$channelId', params: { channelId: result.id } });
      } else {
        // Strip read-only fields. Slug is immutable per backend contract.
        // walkFromMessageId пробрасываем: пустое поле контракт снимает сам,
        // непустое — переставляет курсор застрявшего walk-канала.
        const body = {
          title: values.title,
          cityId: values.cityId,
          isActive: values.isActive,
          fetchCadenceMin: values.fetchCadenceMin,
          kind: values.kind,
          walkFromMessageId: values.walkFromMessageId,
        } as const;
        await update.mutateAsync(body);
        toast.success(t('form.updateSuccess'));
      }
    } catch (err) {
      await mapApiErrorToForm(err, form, { rootField: 'slug' });
    }
  });

  const slugError = form.formState.errors.slug;
  const titleError = form.formState.errors.title;
  const cityError = form.formState.errors.cityId;
  const cadenceError = form.formState.errors.fetchCadenceMin;
  const walkError = form.formState.errors.walkFromMessageId;
  const rootServer = (form.formState.errors.root as { server?: { message?: string } } | undefined)?.server;

  return (
    <form
      onSubmit={readOnly ? (e) => e.preventDefault() : onSubmit}
      className="space-y-4 max-w-xl"
      noValidate
    >
      {isCreate ? (
        <div>
          <Label htmlFor="slug">{t('form.slugLabel')}</Label>
          <Input id="slug" {...form.register('slug')} placeholder={t('form.slugPlaceholder')} />
          <p className="text-xs text-muted-foreground mt-1">
            {t('form.slugHintPart1')}
            <code>_</code>
            {t('form.slugHintPart2')}
            <code>@</code>
            {t('form.slugHintPart3')}
          </p>
          <p className="text-xs text-muted-foreground mt-1">{t('form.slugHintTopic')}</p>
          {slugError && <p className="text-sm text-destructive mt-1">{String(slugError.message)}</p>}
        </div>
      ) : (
        <div>
          <Label htmlFor="slug">{t('form.slugLabel')}</Label>
          <Input
            id="slug"
            value={
              props.channel.topicId > 0
                ? `${props.channel.slug} / ${t('form.topicLabel')} ${props.channel.topicId}`
                : // Именно typeof, а не `!== null`: DTO рантаймом не парсится
                  // (`.json<T>()` — голый каст), и от старого API поле придёт undefined.
                  typeof props.channel.walkCursor === 'number'
                  ? `${props.channel.slug} / ${t('form.walkCursorLabel')} ${props.channel.walkCursor}`
                  : props.channel.slug
            }
            readOnly
            disabled
          />
          <p className="text-xs text-muted-foreground mt-1">{t('form.slugImmutableHint')}</p>
        </div>
      )}

      {(isCreate || typeof props.channel.walkCursor === 'number') && (
        <div>
          <Label htmlFor="walkFromMessageId">
            {isCreate ? t('form.walkLabel') : t('form.walkResetLabel')}
          </Label>
          <Input
            id="walkFromMessageId"
            {...form.register('walkFromMessageId')}
            placeholder={t('form.walkPlaceholder')}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {isCreate ? t('form.walkHint') : t('form.walkResetHint')}
          </p>
          {walkError && <p className="text-sm text-destructive mt-1">{String(walkError.message)}</p>}
        </div>
      )}

      {isCreate ? (
        <p className="text-xs text-muted-foreground">{t('form.titleAutoHint')}</p>
      ) : (
        <div>
          <Label htmlFor="title">{t('form.titleLabel')}</Label>
          <Input
            id="title"
            {...form.register('title')}
            disabled={readOnly}
            placeholder={t('form.titlePlaceholder')}
          />
          {titleError && <p className="text-sm text-destructive mt-1">{String(titleError.message)}</p>}
        </div>
      )}

      <div>
        <Label htmlFor="cityId">{t('form.cityLabel')}</Label>
        <Select
          value={form.watch('cityId') ?? ''}
          disabled={readOnly}
          onValueChange={(v) => form.setValue('cityId', v, { shouldValidate: true })}
        >
          <SelectTrigger id="cityId">
            <SelectValue placeholder={t('form.selectCity')} />
          </SelectTrigger>
          <SelectContent>
            {props.cities.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {cityError && <p className="text-sm text-destructive mt-1">{String(cityError.message)}</p>}
      </div>

      <div>
        <Label htmlFor="kind">{t('form.kindLabel')}</Label>
        <Select
          value={form.watch('kind') ?? 'EVENTS'}
          disabled={readOnly}
          onValueChange={(v) => form.setValue('kind', v as 'EVENTS' | 'NEWS', { shouldValidate: true })}
        >
          <SelectTrigger id="kind">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="EVENTS">{t('kind.events')}</SelectItem>
            <SelectItem value="NEWS">{t('kind.news')}</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">{t('form.kindHint')}</p>
      </div>

      <div>
        <Label htmlFor="fetchCadenceMin">{t('form.cadenceLabel')}</Label>
        <Input
          id="fetchCadenceMin"
          type="number"
          min={5}
          max={1440}
          {...form.register('fetchCadenceMin', { valueAsNumber: true })}
          disabled={readOnly}
        />
        <p className="text-xs text-muted-foreground mt-1">{t('form.cadenceHint')}</p>
        {cadenceError && <p className="text-sm text-destructive mt-1">{String(cadenceError.message)}</p>}
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="isActive"
          checked={form.watch('isActive') ?? true}
          disabled={readOnly}
          onCheckedChange={(v) => form.setValue('isActive', v)}
        />
        <Label htmlFor="isActive">{t('form.activeLabel')}</Label>
      </div>

      {rootServer?.message && <p className="text-sm text-destructive">{rootServer.message}</p>}

      {!readOnly && (
        <Button type="submit" disabled={form.formState.isSubmitting}>
          {isCreate ? t('form.submitCreate') : t('form.submitSave')}
        </Button>
      )}
    </form>
  );
}
